import { useRef, type ReactNode } from 'react';

import { useInfiniteScroll } from '../lib/use-infinite-scroll.ts';

export interface InfiniteFooterProps {
  readonly hasMore: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onLoadMore: () => void;
  readonly onRetry: () => void;
  // Off while a filter is applied: a filtered list can leave the sentinel in
  // view with nothing between it and the top, which would walk the whole
  // history in one burst. The user asks for each extra page instead.
  readonly autoLoad?: boolean;
  readonly endLabel: string;
  readonly loadMoreLabel?: string;
  readonly errorLabel?: string;
}

// Tail of a paged list: arms the scroll sentinel, reports the page in flight,
// offers a retry when a page fails, and states plainly when the list has
// genuinely ended.
export const InfiniteFooter = ({
  hasMore,
  isLoading,
  isError,
  onLoadMore,
  onRetry,
  autoLoad = true,
  endLabel,
  loadMoreLabel = 'Load older transactions',
  errorLabel = 'Could not load more',
}: InfiniteFooterProps): ReactNode => {
  const sentinel = useRef<HTMLDivElement>(null);
  useInfiniteScroll(sentinel, {
    enabled: autoLoad && hasMore && !isLoading && !isError,
    onLoadMore,
  });

  return (
    <div className="list-footer" ref={sentinel}>
      {isError ? (
        <div className="state state--error" role="alert">
          <div>{errorLabel}</div>
          <button type="button" className="retry" onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="list-footer-status" role="status" aria-live="polite" aria-busy="true">
          <span className="loading-spinner" aria-hidden="true" />
          <span className="sr-only">Loading more</span>
        </div>
      ) : hasMore ? (
        <button type="button" className="list-footer-more" onClick={onLoadMore}>
          {loadMoreLabel}
        </button>
      ) : (
        <p className="list-footer-end">{endLabel}</p>
      )}
    </div>
  );
};
